import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import type {
  QIT,
  QuestionGroup,
} from 'src/components/WSCertificationSummary/types/QuestionnaireSchemaTypes';
import type { QuestionnaireAnswersMap } from 'src/services/RenterQuestionnaire.service';

interface VisibilityResult {
  visibleGroupIds: Set<number>;
  visibleQitIds: Set<number>;
  newlyRevealed: Set<number>;
  revealOrigin: Map<number, { questionName: string; choiceName: string }>;
}

function setsEqual(a: Set<number>, b: Set<number>): boolean {
  if (a.size !== b.size) {
    return false;
  }
  for (const v of a) {
    if (!b.has(v)) {
      return false;
    }
  }
  return true;
}

function mapsEqual(
  a: Map<number, { questionName: string; choiceName: string }>,
  b: Map<number, { questionName: string; choiceName: string }>
): boolean {
  if (a.size !== b.size) {
    return false;
  }
  for (const [key, val] of a) {
    const other = b.get(key);
    if (
      !other ||
      other.questionName !== val.questionName ||
      other.choiceName !== val.choiceName
    ) {
      return false;
    }
  }
  return true;
}

function choiceIsSelected(
  answers: QuestionnaireAnswersMap,
  parentQitId: number,
  parentChoiceId: number,
  surveyId: number
): boolean {
  const qitAnswers = answers[parentQitId];
  if (!qitAnswers) {
    return false;
  }
  const surveyAnswers = qitAnswers[surveyId];
  if (!surveyAnswers) {
    return false;
  }
  for (const answer of Object.values(surveyAnswers)) {
    if (answer.question_choices?.some((c) => c.id === parentChoiceId)) {
      return true;
    }
  }
  return false;
}

export function useConditionalVisibility(
  certificationId: number,
  surveyId: number,
  groups: QuestionGroup[],
  qits: QIT[]
): VisibilityResult {
  const answers = useSelector(
    (state: RootState) => state.newRenterInterface.answers[certificationId]
  ) as QuestionnaireAnswersMap | undefined;

  const qitsByIdMap = useMemo(() => {
    const map = new Map<number, QIT>();
    for (const qit of qits) {
      map.set(qit.id, qit);
    }
    return map;
  }, [qits]);

  const visibility = useMemo(() => {
    const currentAnswers = answers ?? {};
    const visibleGroupIds = new Set<number>();
    const visibleQitIds = new Set<number>();
    const revealOrigin = new Map<
      number,
      { questionName: string; choiceName: string }
    >();

    for (const group of groups) {
      if (!group.parent_qit_id || !group.parent_choice_id) {
        visibleGroupIds.add(group.id);
      } else if (
        choiceIsSelected(
          currentAnswers,
          group.parent_qit_id,
          group.parent_choice_id,
          surveyId
        )
      ) {
        visibleGroupIds.add(group.id);
        const parentQit = qitsByIdMap.get(group.parent_qit_id);
        if (parentQit) {
          const choiceName =
            parentQit.question_choices.find(
              (c) => c.id === group.parent_choice_id
            )?.name ??
            parentQit.question.question_choices.find(
              (c) => c.id === group.parent_choice_id
            )?.name ??
            '';
          revealOrigin.set(group.id, {
            questionName: parentQit.question.name,
            choiceName,
          });
        }
      }
    }

    for (const qit of qits) {
      if (!visibleGroupIds.has(qit.question_group_id)) {
        continue;
      }
      if (qit.for_compliance) {
        continue;
      }
      if (qit.is_hidden) {
        continue;
      }
      if (!qit.parent_qit_id || !qit.parent_choice_id) {
        visibleQitIds.add(qit.id);
      } else if (
        choiceIsSelected(
          currentAnswers,
          qit.parent_qit_id,
          qit.parent_choice_id,
          surveyId
        )
      ) {
        visibleQitIds.add(qit.id);
        const parentQit = qitsByIdMap.get(qit.parent_qit_id);
        if (parentQit) {
          const choiceName =
            parentQit.question_choices.find(
              (c) => c.id === qit.parent_choice_id
            )?.name ??
            parentQit.question.question_choices.find(
              (c) => c.id === qit.parent_choice_id
            )?.name ??
            '';
          revealOrigin.set(qit.id, {
            questionName: parentQit.question.name,
            choiceName,
          });
        }
      }
    }

    return { visibleGroupIds, visibleQitIds, revealOrigin };
  }, [answers, groups, qits, qitsByIdMap, surveyId]);

  const [newlyRevealed, setNewlyRevealed] = useState<Set<number>>(
    () => new Set()
  );
  const prevVisibleRef = useRef<Set<number> | null>(null);
  const prevResultRef = useRef<VisibilityResult | null>(null);

  const { visibleGroupIds, visibleQitIds, revealOrigin } = visibility;

  useEffect(() => {
    const currentVisible = new Set([
      ...visibleQitIds,
      ...[...visibleGroupIds].map((id) => -id),
    ]);

    if (prevVisibleRef.current === null) {
      prevVisibleRef.current = currentVisible;
      return;
    }

    const revealed = new Set<number>();
    for (const id of visibleQitIds) {
      if (!prevVisibleRef.current.has(id)) {
        const qit = qitsByIdMap.get(id);
        if (qit?.parent_qit_id) {
          revealed.add(id);
        }
      }
    }
    for (const id of visibleGroupIds) {
      const group = groups.find((g) => g.id === id);
      if (group?.parent_qit_id && !prevVisibleRef.current.has(-id)) {
        revealed.add(-id);
      }
    }

    prevVisibleRef.current = currentVisible;
    setNewlyRevealed(revealed);
  }, [visibleGroupIds, visibleQitIds, groups, qitsByIdMap]);

  return useMemo(() => {
    const prev = prevResultRef.current;
    if (
      prev &&
      setsEqual(prev.visibleGroupIds, visibleGroupIds) &&
      setsEqual(prev.visibleQitIds, visibleQitIds) &&
      setsEqual(prev.newlyRevealed, newlyRevealed) &&
      mapsEqual(prev.revealOrigin, revealOrigin)
    ) {
      return prev;
    }

    const result = {
      visibleGroupIds,
      visibleQitIds,
      newlyRevealed,
      revealOrigin,
    };
    prevResultRef.current = result;
    return result;
  }, [visibleGroupIds, visibleQitIds, newlyRevealed, revealOrigin]);
}
